import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Database,
  Tags,
  Sparkles,
  Bot,
  MessageCircle,
  Zap,
  Workflow,
  ClipboardList,
  Brain,
  Plug,
  Users,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed'

const navigationGroups = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Knowledge',
    items: [
      { name: 'Knowledge Base', href: '/app/knowledge', icon: Database },
      { name: 'Tagsets', href: '/app/tagsets', icon: Tags },
      { name: 'GenClair', href: '/app/genclair', icon: Sparkles },
    ],
  },
  {
    label: 'Ingestion',
    items: [
      // { name: 'Pipelines', href: '/app/ingestion', icon: Workflow },
      { name: 'Triplets', href: '/app/triplets', icon: Workflow },
    ],
  },
  {
    label: 'Assistants',
    items: [
      { name: 'Assistants', href: '/app/assistants', icon: Bot },
      { name: 'SRO', href: '/app/sro', icon: ClipboardList },
      { name: 'Interactions', href: '/app/interactions', icon: MessageCircle },
      { name: 'Actions', href: '/app/actions', icon: Zap },
      { name: 'Memory', href: '/app/memory', icon: Brain },
    ],
  },
  {
    label: 'Administration',
    items: [
      { name: 'Connections', href: '/app/connections', icon: Plug },
      { name: 'Team', href: '/app/team', icon: Users },
      { name: 'Settings', href: '/app/settings', icon: Settings },
    ],
  },
]

interface SidebarProps {
  onCollapsedChange?: (collapsed: boolean) => void
}

export function Sidebar({ onCollapsedChange }: SidebarProps) {
  const location = useLocation()
  const { user, logout, isLoggingOut } = useAuth()
  const { theme, setTheme } = useTheme()

  const toggleTheme = () => {
    if (theme === 'dark') {
      setTheme('light')
    } else {
      setTheme('dark')
    }
  }

  // Initialize collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    return stored === 'true'
  })

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed))
    onCollapsedChange?.(isCollapsed)
  }, [isCollapsed, onCollapsedChange])

  // Notify parent on mount
  useEffect(() => {
    onCollapsedChange?.(isCollapsed)
  }, [])

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => !prev)
  }

  const userInitial = user?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'
  const userEmail = user?.email || 'user@example.com'

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-background border-r flex flex-col transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-60'
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b">
          {!isCollapsed && (
            <Link to="/app/dashboard" className="text-xl font-bold">
              <span className="text-primary">Chatter</span>pillar
            </Link>
          )}
          <div className={cn('flex items-center gap-1', isCollapsed && 'flex-col gap-2')}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  className="h-8 w-8"
                >
                  <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side={isCollapsed ? 'right' : 'bottom'}>
                <p>Toggle theme</p>
              </TooltipContent>
            </Tooltip>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleCollapsed}
              className="h-8 w-8"
            >
              {isCollapsed ? (
                <PanelLeftOpen className="h-5 w-5" />
              ) : (
                <PanelLeftClose className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <div className="space-y-4">
            {navigationGroups.map((group, groupIndex) => (
              <div key={group.label}>
                {/* Section divider & label */}
                {groupIndex > 0 && isCollapsed && (
                  <div className="mx-2 mb-2 border-t border-border" />
                )}
                {!isCollapsed && (
                  <div className={cn('px-3 mb-1', groupIndex > 0 && 'mt-2 pt-2 border-t border-border')}>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                      {group.label}
                    </span>
                  </div>
                )}
                <ul className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon
                    const isActive = location.pathname === item.href || 
                      (item.href !== '/app/dashboard' && location.pathname.startsWith(item.href))

                    const navLink = (
                      <Link
                        to={item.href}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                          isCollapsed && 'justify-center px-2'
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        {!isCollapsed && <span>{item.name}</span>}
                      </Link>
                    )

                    return (
                      <li key={item.name}>
                        {isCollapsed ? (
                          <Tooltip>
                            <TooltipTrigger asChild>{navLink}</TooltipTrigger>
                            <TooltipContent side="right">
                              <p>{item.name}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          navLink
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        {/* User Section */}
        <div className="border-t p-4">
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="flex flex-col gap-2">
                <p className="font-medium">{userEmail}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  disabled={isLoggingOut}
                  className="justify-start"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </Button>
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {userInitial}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-muted-foreground truncate flex-1">
                  {userEmail}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                disabled={isLoggingOut}
                className="justify-start text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </Button>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  )
}
